import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useOrders,
  useOrderStats,
  useCustomerOrganisations,
} from '../hooks';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import {
  buildSalesManagerFilterOptions,
  parseSalesManagerFilterValue,
} from '@/features/accounts/utils/salesManagerFilters';
import { OrderStatsBar } from '../components/OrderStatsBar';
import { OrdersFilters } from '../components/OrdersFilters';
import { OrdersTable, applyOrderColumnFilters } from '../components/OrdersTable';
import { OrderFormModal } from '../components/modals';
import { exportOrdersToCsv } from '../utils/csvExport';
import { ORDER_STATUSES, DEFAULT_VISIBLE_ORDER_COLUMNS } from '../types';
import type {
  Quote,
  QuotesFilters as QuotesFiltersType,
  QuoteStatus,
  OrderColumnKey,
} from '../types';

// Storage keys
const COLUMN_STORAGE_KEY = 'orders-column-visibility';
const COLUMN_FILTER_STORAGE_KEY = 'orders-column-filters';

function getDefaultVisibility(): Record<OrderColumnKey, boolean> {
  const allKeys: OrderColumnKey[] = [
    'quoteNumber', 'status', 'createdAt', 'accountName', 'projectName',
    'salesManager', 'total', 'expirationDate', 'acceptedDate',
    'contractualManager', 'poNumber', 'actions',
  ];
  const defaults: Record<string, boolean> = {};
  for (const key of allKeys) {
    defaults[key] = DEFAULT_VISIBLE_ORDER_COLUMNS.includes(key);
  }
  return defaults as Record<OrderColumnKey, boolean>;
}

function loadColumnVisibility(): Record<OrderColumnKey, boolean> {
  try {
    const stored = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (stored) {
      return { ...getDefaultVisibility(), ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<OrderColumnKey, boolean>) {
  localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibility));
}

export default function OrdersPage() {
  const { t } = useTranslation('orders');

  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customerId, setCustomerId] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState<QuoteStatus[]>(ORDER_STATUSES);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<QuotesFiltersType['sortKey']>('createdAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState<Record<OrderColumnKey, boolean>>(loadColumnVisibility);

  // Column filters (persisted to localStorage)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(COLUMN_FILTER_STORAGE_KEY) || '{}');
      const salesManagerId = parseSalesManagerFilterValue(stored.salesManager);
      return {
        ...stored,
        salesManager: salesManagerId || '',
      };
    }
    catch { return {}; }
  });
  const [salesManagerId, setSalesManagerId] = useState(
    () => parseSalesManagerFilterValue(columnFilters.salesManager) || 'all',
  );

  // Modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Quote | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Persist column filters
  useEffect(() => {
    localStorage.setItem(COLUMN_FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    if (key === 'salesManager') {
      setSalesManagerId(parseSalesManagerFilterValue(value) || 'all');
      setPage(0);
    }
  };

  // Promote text column filters to server-side search (skip date-range ">>" and select "[")
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([key, value]) => {
        if (!value) return false;
        if (key === 'salesManager') return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      if (columnSearchText) setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  const combinedSearch = [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ') || undefined;

  // Build filters
  const filters: QuotesFiltersType = {
    search: combinedSearch,
    accountId: customerId !== 'all' ? customerId : undefined,
    salesManagerId: salesManagerId !== 'all' ? salesManagerId : undefined,
    status_in: selectedStatuses.length > 0 ? selectedStatuses : ORDER_STATUSES,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  // Queries
  const ordersQuery = useOrders(filters);
  const statsQuery = useOrderStats();
  const customersQuery = useCustomerOrganisations();
  const salesManagersQuery = useSalesManagers();

  const serverOrders = ordersQuery.data?.quotes || [];
  const stats = statsQuery.data || [];
  const total = ordersQuery.data?.total || 0;

  // Client-side column filtering (date-range, number-range, select refine within page)
  const orders = useMemo(
    () => applyOrderColumnFilters(serverOrders, columnFilters),
    [serverOrders, columnFilters],
  );
  const customers = customersQuery.data || [];
  const salesManagerOptions = buildSalesManagerFilterOptions(
    salesManagersQuery.data || [],
  );

  // Handlers
  const handleSort = (key: QuotesFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(-1);
    }
    setPage(0);
  };

  const handleCustomerChange = (value: string) => {
    setCustomerId(value);
    setPage(0);
  };

  const handleSalesManagerChange = (value: string) => {
    setSalesManagerId(value);
    setColumnFilters((prev) => ({
      ...prev,
      salesManager: value === 'all' ? '' : value,
    }));
    setPage(0);
  };

  const handleStatusChange = (statuses: QuoteStatus[]) => {
    setSelectedStatuses(statuses);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleColumnToggle = (key: OrderColumnKey) => {
    if (key === 'quoteNumber') return; // Always visible
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const handleEditClick = (order: Quote) => {
    if (!order.mongoQuoteId) {
      toast.error(t('toast.cannotEdit'));
      return;
    }
    setEditingOrder(order);
    setIsFormOpen(true);
  };

  const handleCreateClick = () => {
    setEditingOrder(null);
    setIsFormOpen(true);
  };

  const handleExport = () => {
    try {
      exportOrdersToCsv(orders, 'orders');
      toast.success(t('toast.exportSuccess', { count: orders.length }));
    } catch {
      toast.error(t('toast.exportError'));
    }
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="breadcrumb flex items-center gap-1.5">
        <Link to="/erp" className="text-atek-blue hover:text-atek-blue-dark">
          {t('page.erp')}
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground">{t('page.title')}</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t('page.title')}</h1>
          <p className="text-[13px] text-muted-foreground mt-1">{t('page.subtitle')}</p>
        </div>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          {t('page.newOrder')}
        </Button>
      </div>

      {/* Stats Bar */}
      {stats.length > 0 && <OrderStatsBar stats={stats} />}

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('page.allOrders')}</CardTitle>
              <CardDescription>
                {orders.length < serverOrders.length
                  ? `${orders.length} of ${serverOrders.length} on this page`
                  : t('page.ordersFound', { count: total })}
              </CardDescription>
            </div>
            <OrdersFilters
              search={search}
              onSearchChange={setSearch}
              customerId={customerId}
              onCustomerChange={handleCustomerChange}
              salesManagerId={salesManagerId}
              onSalesManagerChange={handleSalesManagerChange}
              selectedStatuses={selectedStatuses}
              onStatusChange={handleStatusChange}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              customers={customers}
              isLoadingCustomers={customersQuery.isLoading}
              salesManagers={salesManagersQuery.data || []}
              isLoadingSalesManagers={salesManagersQuery.isLoading}
              onExport={handleExport}
              columnVisibility={columnVisibility}
              onColumnToggle={handleColumnToggle}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ordersQuery.error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">{t('page.errorLoading')}</p>
            </div>
          ) : (
            <OrdersTable
              orders={orders}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              onEdit={handleEditClick}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              columnVisibility={columnVisibility}
              columnFilters={columnFilters}
              setColumnFilter={setColumnFilter}
              salesManagerOptions={salesManagerOptions}
              isLoading={ordersQuery.isLoading}
            />
          )}
        </CardContent>
      </Card>

      {/* Form Modal */}
      <OrderFormModal
        open={isFormOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsFormOpen(false);
            setEditingOrder(null);
          }
        }}
        editOrder={editingOrder}
      />
    </div>
  );
}
